import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion, useInView } from 'framer-motion';
import Icon from '../common/Icon';
import './Footer.css';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  return (
    <footer className="footer">
      <Container>
        <motion.div
          ref={ref}
          className="footer-content"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <Row>
            <Col lg={4} md={6} className="footer-about mb-4 mb-lg-0">
              <motion.h3 variants={itemVariants}>Muhammad Rahim</motion.h3>
              <motion.p variants={itemVariants}>
                A Computer Science student at FAST NUCES with a passion for web development
                and software engineering, creating innovative solutions to complex problems.
              </motion.p>
              <motion.div className="social-links" variants={itemVariants}>
                {[
                  { url: "https://www.linkedin.com/in/muhammadrahimusman/", icon: "FaLinkedin" },
                  { url: "https://github.com/Rebelhere", icon: "FaGithub" },
                  { url: "https://www.instagram.com/rahim.usman.77/", icon: "FaInstagram" },
                  { url: "mailto:muhammadrahimusman@gmail.com", icon: "FaEnvelope" }
                ].map((social, index) => (
                  <motion.a 
                    key={index}
                    href={social.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1, y: -5 }}
                    whileTap={{ scale: 0.9 }}
                    className="social-icon"
                    custom={index}
                    variants={{
                      hidden: { opacity: 0, y: 20 },
                      visible: (i) => ({
                        opacity: 1,
                        y: 0,
                        transition: { 
                          delay: 0.3 + i * 0.1,
                          type: "spring",
                          stiffness: 200
                        }
                      })
                    }}
                    style={{ color: "var(--footer-social-color, #222)" }} // fallback to dark color
                  >
                    <Icon icon={social.icon} />
                  </motion.a>
                ))}
              </motion.div>
            </Col>

          <Col lg={4} md={6} className="footer-nav mb-4 mb-lg-0">
            <motion.h3 variants={itemVariants}>Quick Links</motion.h3>
            <motion.ul className="footer-links" variants={containerVariants} initial="hidden" animate={isInView ? "visible" : "hidden"}>
              {[
                { href: "#", label: "Home" },
                { href: "#", label: "About" },
                { href: "#", label: "Skills" },
                { href: "#", label: "Projects" },
                { href: "#", label: "Contact" }
              ].map((link, index) => (
                <motion.li
                  key={index}
                  custom={index}
                  variants={{
                    hidden: { opacity: 0, x: -20 },
                    visible: {
                      opacity: 1,
                      x: 0,
                      transition: { 
                        delay: 0.4 + index * 0.1,
                        type: "spring",
                        stiffness: 100
                      }
                    }
                  }}
                  initial="hidden"
                  animate={isInView ? "visible" : "hidden"}
                >
                  <a href={link.href}>{link.label}</a>
                </motion.li>
              ))}
            </motion.ul>
          </Col>

          <Col lg={4} md={12} className="footer-contact">
            <motion.h3 variants={itemVariants}>Contact Me</motion.h3>
            {[
              { type: "Email:", value: "muhammadrahimusman@gmail.com" },
              { type: "Phone:", value: "Available on Request" },
              { type: "Location:", value: "Lahore, Pakistan" }
            ].map((contact, index) => (
              <motion.div 
                key={index}
                className="contact-item"
                custom={index}
                variants={{
                  hidden: { opacity: 0, x: 20 },
                  visible: (i) => ({
                    opacity: 1,
                    x: 0,
                    transition: { 
                      delay: 0.5 + i * 0.1,
                      type: "spring",
                      stiffness: 100
                    }
                  })
                }}
              >
                <span>{contact.type}</span>
                <p>{contact.value}</p>
              </motion.div>
            ))}
          </Col>
          </Row>

          <motion.div 
            className="footer-bottom"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ delay: 0.8, duration: 0.5 }}
          >
            <motion.p
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300, damping: 10 }}
            >
              © {currentYear} Muhammad Rahim. All rights reserved.
            </motion.p>
          </motion.div>
        </motion.div>
      </Container>
    </footer>
  );
};

export default Footer;
